import {getTestProps} from '@benchling/js-test-utils';
import {css, StyleSheet} from 'aphrodite';
import assert from 'assert';
import React, {useCallback, useState} from 'react';

import {trackEvent} from 'analytics/Analytics';
import BenchlingConfig from 'BenchlingConfig';
import {LLM_CHAT_ANALYTICS_ACTIONS} from 'constants/analytics-products/LLMChatConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {ChatType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {getActiveWorkspaceEntry, openAddToEntryModal} from 'llm/modals/AddToEntryModal';
import DocumentEditor from 'notebook/documents/DocumentEditor';
import notifications from 'notifications';
import {FONT_PROXIMA_18} from 'styles/FontConstants';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import BenchChip from 'ui-library/chips/BenchChip';
import {ChipSize} from 'ui-library/chips/Chip';
import {GRAY_LIGHT, WHITE} from 'ui-library/Colors';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import legacyRunModal from 'ui-library/legacyRunModal';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';

import {DraftDocument} from './DataCaptureTypes';
import {SaveAsDocumentModal} from './SaveAsDocumentModal';

export interface DataCaptureDraftDocumentProps {
  draftDocument: DraftDocument;
  isDocumentEditable: boolean;
  locked: boolean;
  // If present, we show an X close button in the top right corner and call this callback
  // when clicked. Otherwise, no X is shown.
  onClose?: () => void;
}

export function DataCaptureDraftDocument({
  draftDocument,
  isDocumentEditable,
  onClose,
  locked,
}: DataCaptureDraftDocumentProps): JSX.Element {
  const documentId = draftDocument.document_api_identifier;
  const sourceDocumentId = draftDocument.source_document_api_identifier;
  const [isSaving, setIsSaving] = useState(false);

  const handleAddToEntryClick = async (): Promise<void> => {
    const result = await openAddToEntryModal({
      getContentsToInsert: () => Promise.resolve({sourceDocumentApiId: documentId}),
      initialSelectedEntry: getActiveWorkspaceEntry(),
    });
    if (result) {
      notifications.success('Content has been added to the selected entry!');
    }
  };

  const handleSaveAsClick = useCallback(
    async (targetType: string): Promise<void> => {
      assert(documentId, 'Document to save from was not found.');
      setIsSaving(true);

      try {
        await legacyRunModal(SaveAsDocumentModal, {
          documentId,
          documentName: 'Untitled Document',
          targetType,
          sourceDocumentId,
          onRequestSubmit: (result) => {
            trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.SAVED_LLM_GENERATED_DOCUMENT, {
              saveType: targetType,
              chatType: ChatType.DATA_CAPTURE,
            });
          },
        });
      } finally {
        setIsSaving(false);
      }
    },
    [documentId, sourceDocumentId]
  );

  const saveDropdownMenu = (
    <Menu height={Menu.HEIGHTS.SMALL} width={Menu.WIDTHS.MEDIUM} dropLeft={true}>
      {BenchlingConfig.ELN_ENABLE_BLANK_ENTRY_CREATION ? (
        <Option
          value={ModelTypeConstants.ENTRY}
          onSelect={() => handleSaveAsClick(ModelTypeConstants.ENTRY)}
          icon="entry"
        >
          Save as Entry
        </Option>
      ) : null}
      <Option
        value={ModelTypeConstants.TEMPLATE}
        onSelect={() => handleSaveAsClick(ModelTypeConstants.TEMPLATE)}
        icon="template"
      >
        Save as Template
      </Option>
      <Option value="add-to-entry" onSelect={handleAddToEntryClick} icon="plus">
        Add to Existing Entry
      </Option>
    </Menu>
  );

  const saveButton = (
    <Button
      buttonStyle={ButtonStyle.PRIMARY}
      size={ButtonSize.SMALL}
      text="Save as..."
      isDropdown={true}
      disabled={!documentId || isSaving}
    />
  );

  return (
    <div
      className={css(styles.documentContainer)}
      {...getTestProps({
        component: 'DataCaptureDraftDocument',
        element: 'documentContainer',
      })}
    >
      <div
        className={css(styles.documentHeader, isDocumentEditable && styles.documentHeaderEditable)}
        {...getTestProps({component: 'DataCaptureDraftDocument', element: 'documentHeader'})}
      >
        <div className={css(styles.headerLeft)}>
          <div className={css(styles.documentTitle)}>Draft Document</div>
          {sourceDocumentId != null && sourceDocumentId !== '' && (
            <div {...getTestProps({component: 'DataCaptureDraftDocument', element: 'sourceDocumentChip'})}>
              <BenchChip id={sourceDocumentId} size={ChipSize.SMALL} />
            </div>
          )}
        </div>
        <div className={css(styles.headerActions)}>
          {locked ? null : <Dropdown menu={saveDropdownMenu}>{saveButton}</Dropdown>}
          {onClose && (
            <BenchlingIcon
              icon="close"
              onClick={onClose}
              dataTestAttrs={{component: 'DataCaptureDraftDocument', element: 'closeButton'}}
            />
          )}
        </div>
      </div>
      <div
        className={css(styles.documentContent)}
        {...getTestProps({
          component: 'DataCaptureDraftDocument',
          element: 'documentContent',
        })}
      >
        <DocumentEditor
          id={documentId}
          locked={!isDocumentEditable}
          disableSplitscreen={true}
          disableProtectionIndicator={true}
          disablePerformanceIndicator={true}
          manageRealtimeUpdates={true}
        />
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  documentContainer: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    flex: 1,
    backgroundColor: WHITE,
  },
  documentHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    backgroundColor: WHITE,
  },
  // When the document is editable, there is a visible toolbar, so show a bottom border and some
  // spacing. In read-only mode, there is no toolbar, so the bottom border would make an ugly
  // double-border.
  documentHeaderEditable: {
    borderBottom: `1px solid ${GRAY_LIGHT}`,
    // TODO: We add this margin to avoid an ugly asymmetry in the toolbar. When the toolbar is fixed
    // to avoid excessive bottom padding, we should remove this.
    marginBottom: 12,
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  documentTitle: {
    margin: 0,
    ...FONT_PROXIMA_18,
  },
  documentContent: {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
});
